<template>
    <div>
        <ts-page-title
            :title="$t('jobDetail.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('jobDetail.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <Button type="info" ghost @click="addNew">{{
                    $t("addNew")
                }}</Button>
                <div class="tw-flex tw-items-center tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('jobDetail.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        enter-button
                        @on-search="onSearch"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="positionLevel">
                    {{
                        row.position_level
                            ? row.position_level.position_level_name_en
                            : ""
                    }}
                </template>
                <template slot-scope="{ row }" slot="job_title">
                    {{ row.job_title ? row.job_title.job_title_en : "" }}
                </template>
                <template slot-scope="{ row }" slot="reportToPosition">
                    {{
                        row.report_to_position
                            ? row.report_to_position.position_name_en
                            : ""
                    }}
                </template>
                <template slot-scope="{ row }" slot="orgChartStructure">
                    {{
                        row.org_chart_structure
                            ? row.org_chart_structure.org_chart_structure
                            : ""
                    }}
                </template>
                <template slot-scope="{ row }" slot="is_head">
                    <Icon
                        v-if="row.is_head"
                        type="ios-checkmark-circle-outline text-success"
                        size="20"
                    />
                    <Icon
                        v-else
                        type="ios-close-circle-outline text-danger"
                        size="20"
                    />
                </template>
                <template slot-scope="{ row }" slot="is_allow_ot_claim">
                    <Icon
                        v-if="row.is_allow_ot_claim"
                        type="ios-checkmark-circle-outline text-success"
                        size="20"
                    />
                    <Icon
                        v-else
                        type="ios-close-circle-outline text-danger"
                        size="20"
                    />
                </template>
                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip
                        confirm
                        :title="$t('areYouSure')"
                        @on-ok="onDelete(row)"
                        :transfer="true"
                        :ok-text="$t('yes')"
                        :cancel-text="$t('no')"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._deleting"
                            ></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                    </Poptip>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>

            <!-- form-action -->
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('jobDetail.pageTitle')"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { mapState } from "vuex";
import FromAction from "./form";

export default {
    name: "job-detailIndex",
    components: {
        FromAction
    },
    data() {
        return {
            loading: false,
            showForm: false,
            width: "width: 300px"
        };
    },
    computed: {
        ...mapState("humanResource/jobDetail", ["resources", "pagination"]),
        search: {
            get() {
                return this.$store.state.humanResource.jobDetail.search;
            },
            set(newValue) {
                this.$store.commit(
                    "humanResource/jobDetail/SET_SEARCH",
                    newValue
                );
                this.$store.commit(
                    "humanResource/jobDetail/RESET_CURRENT_PAGE"
                );
            }
        },
        columns() {
            return [
                {
                    title: this.$t("jobDetail.jobDetailKhTitle"),
                    key: "position_name_kh",
                    minWidth: 200
                },
                {
                    title: this.$t("jobDetail.jobDetailEnTitle"),
                    key: "position_name_en",
                    minWidth: 200
                },
                {
                    title: this.$t("jobDetail.jobTitleLevelTitle"),
                    slot: "positionLevel",
                    minWidth: 200
                },
                {
                    title: this.$t("jobDetail.jobTitleTitle"),
                    slot: "job_title",
                    minWidth: 200
                },
                {
                    title: this.$t("jobDetail.reportToTitle"),
                    slot: "reportToPosition",
                    minWidth: 200
                },
                {
                    title: this.$t("jobDetail.orgChartStructureTitle"),
                    slot: "orgChartStructure",
                    minWidth: 250
                },
                {
                    title: this.$t("jobDetail.isHeadTitle"),
                    slot: "is_head",
                    minWidth: 100,
                    align: "center"
                },
                {
                    title: this.$t("jobDetail.isAllowOtClaimTitle"),
                    slot: "is_allow_ot_claim",
                    minWidth: 150,
                    align: "center"
                },
                {
                    title: this.$t("actions"),
                    key: "action",
                    slot: "action",
                    fixed: "right",
                    align: "center",
                    className: "tw-space-x-2",
                    width: 85
                }
            ];
        }
    },
    methods: {
        onSearch () { if (!this.search) return; this.fetchData() },
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("humanResource/jobDetail/fetch", {
                    ...attributes
                })
                .then(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.notice({ type: "error", text: error.message });
                });
        },
        onEdit(record) {
            this.$store.commit("humanResource/jobDetail/SET_EDIT_DATA", record);
            this.showForm = true;
            this.$refs.form_action.setEditData();
        },
        clearEdit() {
            this.showForm = false;
            this.$refs.form_action.setEditData();
            this.$refs.form_action.clearInput();
            this.$store.commit("humanResource/jobDetail/SET_EDIT_DATA", {});
        },
        addNew() {
            this.$store.commit("humanResource/jobDetail/SET_EDIT_DATA", {});
            this.showForm = true;
            this.$refs.form_action.setEditData();
            this.$refs.form_action.clearInput();
        },

        async onDelete(record) {
            record._deleting = true;
            this.$store
                .dispatch("humanResource/jobDetail/destroy", record.position_id)
                .then(response => {
                    this.fetchData();
                    record._deleting = false;
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                    record._deleting = false;
                });
        },

        notice(not) {
            this.$Notice[not.type]({
                title: "JOB DETAIL",
                desc: not.text
            });
        }
    },
    watch: {},
    beforeRouteLeave(to, from, next) {
        this.$store.commit("humanResource/jobDetail/RESET_STATE");
        next();
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData();
        });
    }
};
</script>
